import { Auth } from 'aws-amplify';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';

import { OnboardingAuthContentWrapper } from 'src/legacy/components/Onboarding/Login/OnboardingAuth';
import {
  OnboardingLoginChallenge,
  onboardingLoginChallengeFormSchema,
} from 'src/legacy/components/Onboarding/OnboardingForms/OnboardingLoginChallengeForm';
import { useSetFormPageInfo } from 'src/routes/onboarding/setup/context/formPageInfoContext';
import {
  OnboardingFormValues,
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { TrackEvent } from 'src/utils/AnalyticsUtils';
import { CognitoUserWithAttributes } from 'src/utils/CognitoUtils';

const CHALLENGE_NAME = 'SOFTWARE_TOKEN_MFA';

export const LoginChallenge = ({
  trackedEvents,
  onAnalyticsEventTracked,
  authenticatedUser,
}: {
  trackedEvents: TrackedEventsType;
  onAnalyticsEventTracked: (eventName: OnboardingStep) => void;
  authenticatedUser?: CognitoUserWithAttributes;
}) => {
  const setFormPageInfo = useSetFormPageInfo<OnboardingFormValues>();
  useEffect(() => {
    setFormPageInfo({
      submitHandler: async ({
        values: submitValues,
        formikHelpers,
        identifyUser,
      }) => {
        try {
          const user: CognitoUserWithAttributes | null =
            await Auth.confirmSignIn(
              authenticatedUser,
              submitValues.challengeResponse,
              CHALLENGE_NAME,
            );
          if (user) {
            // set the onboarding values name attribute to the user name
            const { given_name, family_name } = user.attributes;
            const newValues: OnboardingFormValues = {
              ...submitValues,
              name: `${given_name} ${family_name}`,
            };
            formikHelpers.setValues(newValues);
            identifyUser(newValues);

            if (!trackedEvents['login-challenge']) {
              await TrackEvent('Onboarding - MFA Login Challenge', {
                email: newValues.email,
                name: newValues.name,
              });

              // update the tracked events
              onAnalyticsEventTracked('who-you-serve');
            }
          } else {
            throw new Error('returned cognito user is null');
          }
        } catch (err: any) {
          const errorName: string = err?.name || '';

          if (errorName === 'ExpiredCodeException') {
            formikHelpers.setFieldError(
              'challengeResponse',
              'This code has already been used. Please enter a new code from your authenticator app.',
            );
          }

          throw err;
        }
      },
      validationSchema: onboardingLoginChallengeFormSchema,
    });
  }, [
    setFormPageInfo,
    authenticatedUser,
    onAnalyticsEventTracked,
    trackedEvents,
  ]);

  const { values } = useFormikContext<OnboardingFormValues>();

  return (
    <OnboardingAuthContentWrapper
      challengeParam={{ type: CHALLENGE_NAME, email: values.email }}
    >
      <OnboardingLoginChallenge challengeName={CHALLENGE_NAME} />
    </OnboardingAuthContentWrapper>
  );
};
